import type { SpaceResource, UserOnboardingResource } from "@octopusdeploy/octopus-server-client";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import { useState } from "react";
import type { ProjectStatus } from "~/areas/projects/components/ProjectStatus/useProjectStatus";
import { repository } from "~/clientInstance";
import type { DataBaseComponentState } from "~/components/DataBaseComponent";
import DataBaseComponent from "~/components/DataBaseComponent";
import { WelcomeToOctopus } from "~/components/OnboardingDialog/WelcomeToOctopus";
import { createWelcomeDialogDismissedAnalyticsEvent } from "./Analytics/WelcomeDialogAnalytics";
export type WelcomeDialogStates = "none" | "welcome-dialog" | "new-project-dialog";
export interface WelcomeDialogInternalProps {
    userOnboarding: UserOnboardingResource | undefined;
    currentSpace: SpaceResource;
    onDialogClosed: () => void;
    projectStatus: ProjectStatus;
}
export function WelcomeDialogInternal(props: WelcomeDialogInternalProps) {
    const { userOnboarding, currentSpace, onDialogClosed } = props;
    const trackEvent = useTrackEvent();
    const [activeDialog, setActiveDialog] = useState<WelcomeDialogStates>("welcome-dialog");
    const setState = (value: WelcomeDialogStates) => {
        if (value === "none")
            onDialogClosed();
        setActiveDialog(value);
    };
    const dismissDialog = () => {
        trackEvent(createWelcomeDialogDismissedAnalyticsEvent());
        setState("none");
    };
    if (!userOnboarding || !userOnboarding.IsTrialInstance || userOnboarding.WelcomeDialogDismissedDate !== null)
        return <></>;
    return <WelcomeToOctopus open={activeDialog === "welcome-dialog"} onClose={dismissDialog} userOnboarding={userOnboarding} currentSpace={currentSpace} projectStatus={props.projectStatus}/>;
}
interface WelcomeDialogState extends DataBaseComponentState {
    userOnboarding?: UserOnboardingResource;
    currentSpace?: SpaceResource;
}
type WelcomeDialogProps = {
    projectStatus: ProjectStatus;
};
export class WelcomeDialog extends DataBaseComponent<WelcomeDialogProps, WelcomeDialogState> {
    constructor(props: WelcomeDialogProps) {
        super(props);
        this.state = {};
    }
    async componentDidMount() {
        await this.doBusyTask(async () => {
            if (!repository.spaceId)
                return;
            const [response, space] = await Promise.all([repository.UserOnboarding.getForCurrentUser(), repository.Spaces.get(repository.spaceId)]);
            this.setState({ userOnboarding: response?.UserOnboardingResource, currentSpace: space });
        });
    }
    async handleDialogClosed() {
        if (this.state.userOnboarding) {
            const updated: UserOnboardingResource = { ...this.state.userOnboarding, WelcomeDialogDismissedDate: new Date() };
            this.setState({ userOnboarding: updated });
            this.doBusyTask(async () => await repository.UserOnboarding.modifyForCurrentUser(updated));
        }
    }
    render() {
        const { currentSpace, userOnboarding } = this.state;
        if (!currentSpace) {
            return null;
        }
        return <WelcomeDialogInternal userOnboarding={userOnboarding} currentSpace={currentSpace} onDialogClosed={() => this.handleDialogClosed()} projectStatus={this.props.projectStatus}/>;
    }
    static displayName = "WelcomeDialog";
}
